import { InfoCircleOutlined } from '@ant-design/icons';
import { ButtonProps, Card, Flex, Tooltip, Typography } from 'antd';
import { GREEN, ORANGE, RED, YELLOW } from 'components/gauge/Gauge';
import { sortBy } from 'lodash/fp';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { SymptomCardProps } from './types';

/**
 * Styles that can only be done in CSS go in here
 */
import clsx from 'clsx';
import { BaseButton } from 'components/button/button-base';
import { Text, Title } from 'components/mvp-typography';
import { StaticBrainFunctionVisualisation } from 'lib/utils/business/brainfunction-categories/types';
import { ErrorBoundary } from 'react-error-boundary';
import { v4 } from 'uuid';
import { CurrentStatus } from './components/current-status';
import { HistoricalStatus } from './components/historical-status';
import './symptom-card-2.scss';

export const SymptomCard2Context = createContext<{
  showNumbers: boolean;
  setShowNumbers?: React.Dispatch<React.SetStateAction<boolean>>;
}>({ showNumbers: false, setShowNumbers: () => !!0 });
export const SymptomCard2ContextProvider = SymptomCard2Context.Provider;
export const SymptomCard2ContextConsumer = SymptomCard2Context.Consumer;

export const BRAINFUNCTION_COLOR_SCHEME = [GREEN, YELLOW, ORANGE, RED];
const colors = [GREEN, YELLOW, ORANGE, RED];

export function useCreateSymptomCardContextState() {
  const [showNumbers, setShowNumbers] = useState<boolean>(false);

  return {
    showNumbers,
    setShowNumbers
  };
}

export function useSymptomCardContext() {
  return useContext(SymptomCard2Context);
}

interface SymptomCard2Props extends SymptomCardProps {
  brainFunction: StaticBrainFunctionVisualisation;
  showMoreInfo: boolean;
  onMoreInfoClick: () => void;
}

/**
 * An improved symptom card
 *
 * Shows BrainFunction metrics
 * 1. Unflipped: A gauge to display the current value
 * 2. Flipped: A bar chart to display historical values
 */
export function SymptomCard2(props: SymptomCard2Props) {
  const globalSymptomCard2Context = useSymptomCardContext();

  /**
   * Controls if the card is flipped to view more details
   */
  const [flipped, setFlipped] = useState(false);
  /**
   * Controls if numbers should be displayed
   */
  const [showNumbers, setShowNumbers] = useState(false);

  /**
   * Override showNumbers when the value in global context is updated
   */
  useEffect(() => {
    setShowNumbers(globalSymptomCard2Context.showNumbers);
  }, [globalSymptomCard2Context.showNumbers]);

  /**
   * Sort the visualisation data
   *
   * WARNING: This should happen when the data is collected
   */
  const sortedData = useMemo(
    () => sortBy(d => d.date, props.data),
    [props.data]
  );
  /**
   * Create an ID so that the parent card can be identified in on-click events
   *
   * We could also use a React `ref` object
   */
  const instanceId = useMemo(() => v4(), []);

  return (
    <Card
      id={instanceId}
      className={clsx('symptom-card-2', flipped ? 'flipped' : '')}
      style={{
        /**
         * WARNING: Hardcoded width
         */
        width: 250
      }}
    >
      <SymptomCard2ContextProvider value={{ showNumbers: showNumbers }}>
        <Flex
          justify={'space-between'}
          align="center"
          style={{
            height: 20
          }}
        >
          {
            /**
             * Render a button to show more information
             */
            props.showMoreInfo ? (
              <Tooltip title="This Brainfunction is composed of multiple scores. Click to view the breakdown.">
                <ShowMoreButton
                  onClick={e => {
                    e.preventDefault();
                    props.onMoreInfoClick();
                  }}
                >
                  <InfoCircleOutlined />
                </ShowMoreButton>
              </Tooltip>
            ) : (
              <div />
            )
          }

          {/* <ToggleWithLabel
            className="print-display-none"
            checked={showNumbers}
            setChecked={setShowNumbers}
          /> */}
        </Flex>
        <div
          onClick={event => {
            event.preventDefault();
            setFlipped(old => !old);
          }}
        >
          <Flex justify={'center'}>
            <Typography.Title level={3}>
              <b>{props.label}</b>
            </Typography.Title>
          </Flex>

          {/* Render the card graphs */}
          <ErrorBoundary
            fallbackRender={({ error }) => (
              <>
                <Title>Rendering error</Title>
                <Text>{error.message}</Text>
              </>
            )}
          >
            {flipped ? (
              <HistoricalStatus
                sortedData={sortedData}
                scale={props.brainFunction.scale}
                colors={colors}
              />
            ) : (
              <CurrentStatus
                scale={props.brainFunction.scale}
                colors={colors}
                sortedData={sortedData}
              />
            )}

            {
              /**
               * Scores that are "raw" are unmodified percentiles
               */
              !props.brainFunction.isRawScore && showNumbers && (
                <Text
                  style={{ color: '#767679', paddingTop: '10px' }}
                  className="crx-typography small-normal"
                >
                  *The score presented is a percentile
                </Text>
              )
            }
          </ErrorBoundary>
        </div>
      </SymptomCard2ContextProvider>
    </Card>
  );
}

function ShowMoreButton(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      style={{
        padding: '0.4rem',
        margin: 0,
        background: 'transparent',
        color: 'black',
        ...props.style
      }}
    />
  );
}
